import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EXPORT_SLIDE_W } from '../utils/slideFrames.js';

/**
 * SlideFlowStage
 * --------------
 * Lays a flow-mode slide out at one fixed width and scales it to fit.
 *
 * A flow slide is a document: blocks stack, and the box they stack in is a
 * fluid 16:9. The trouble is that the things inside them are measured in three
 * different systems. Body text and headings are relative, so they track the
 * box; Tailwind's utilities are rem, which is fixed; and the image renderer's
 * `max-w-md` is a hard 448px cap. Measured on a heading-plus-bullets-plus-image
 * slide, widening the window until the slide was 1.667x wider grew the text by
 * 1.735 and the image by exactly 1.000 — a 40% divergence. The picture stayed
 * the size it had always been while the words grew around it, so the columns
 * re-balanced, the text re-wrapped, and the slide read differently full screen
 * than windowed.
 *
 * Laying out at a constant width and applying a single transform makes the unit
 * question moot: px, rem, em and cqw all scale by the same factor because the
 * whole stage does. This is the same treatment the canvas designer already
 * gets, and what the PowerPoint and PDF rasterisers do.
 *
 * Width, not width-and-height. A flow slide's height follows its content — it
 * is allowed to run past 16:9 and scroll — so pinning a height here would clip
 * long slides. Scaling by width alone keeps every proportion while letting the
 * slide be as tall as it needs to be.
 */
const CSS_PX_PER_IN = 96;
export const FLOW_STAGE_WIDTH = Math.round(EXPORT_SLIDE_W * CSS_PX_PER_IN);

/**
 * Ignore churn too small to see, which would otherwise bounce between two
 * values on every resize observation.
 *
 * Two thresholds, because the two quantities are on completely different
 * scales: the reserved height is in pixels, where half a pixel is nothing,
 * while the scale factor is a ratio around 0-1, where half is the difference
 * between a windowed slide and a full-screen one. Sharing one tolerance meant
 * every real scale change was smaller than the threshold and got dropped —
 * the reserved height tracked the window while the stage inside it never
 * resized.
 */
const HEIGHT_EPSILON = 0.5;
const SCALE_EPSILON = 0.0005;
const changed = (previous, next, epsilon) =>
  previous === null || Math.abs(previous - next) > epsilon;

const SlideFlowStage = ({ children, className = '' }) => {
  const fitRef = useRef(null);
  const stageRef = useRef(null);
  const [scale, setScale] = useState(null);
  const [height, setHeight] = useState(null);

  const measure = useCallback(() => {
    const fit = fitRef.current;
    const stage = stageRef.current;
    if (!fit || !stage) return;
    const width = fit.clientWidth;
    if (width <= 0) return;
    const next = width / FLOW_STAGE_WIDTH;
    // scrollHeight is layout height, which a transform does not affect — so
    // reading it here cannot feed back into the scale that produced it.
    const nextHeight = stage.scrollHeight * next;
    setScale((prev) => (changed(prev, next, SCALE_EPSILON) ? next : prev));
    setHeight((prev) => (changed(prev, nextHeight, HEIGHT_EPSILON) ? nextHeight : prev));
  }, []);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    if (fitRef.current) observer.observe(fitRef.current);
    // Watch the stage too: editing a block changes the slide's height without
    // changing the width this scales from.
    if (stageRef.current) observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, [measure]);

  // Images decode after layout, and a late decode changes the height. Without
  // this the reserved space stays at whatever it was before the picture
  // arrived, and the slide is clipped or leaves a gap.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const images = Array.from(stage.querySelectorAll('img'));
    const pending = images.filter((image) => !image.complete);
    if (pending.length === 0) return undefined;
    pending.forEach((image) => {
      image.addEventListener('load', measure);
      image.addEventListener('error', measure);
    });
    return () => pending.forEach((image) => {
      image.removeEventListener('load', measure);
      image.removeEventListener('error', measure);
    });
  });

  return (
    <div
      ref={fitRef}
      className={`slide-flow-fit ${className}`.trim()}
      style={height === null ? undefined : { height: `${height}px` }}
    >
      <div
        ref={stageRef}
        className="slide-flow-stage"
        style={{
          width: `${FLOW_STAGE_WIDTH}px`,
          // Hidden until measured, so a full-width slide never flashes across
          // the page on first paint.
          transform: `scale(${scale === null ? 0 : scale})`
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SlideFlowStage;
